// src/components/StickyCapacitate.jsx
import { useEffect, useRef, useState } from "react";
import { GrScheduleNew } from "react-icons/gr";
import Flyer1 from "../assets/PNA1.jpg";
import Flyer2 from "../assets/PNA2.jpg";

export default function StickyPNA({
  intervalMs = 5500,
  label = "Programa Nacional de Actualización",
  maxCardWidth = 760,
  /** 🔹 cuánto subir el botón respecto al borde inferior */
  shiftUpPx = 50,
  /** 🔹 límites del visor para que no sobresalga en escritorio/móvil */
  viewportMaxWidthPx = 420,
  viewportMaxHeightVh = 80,
}) {
  const images = [
    { src: Flyer1, alt: "Flyer 1" },
    { src: Flyer2, alt: "Flyer 2" },
  ];

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [ar, setAr] = useState("9 / 16"); // relación de aspecto del slide actual
  const timerRef = useRef(null);

  // Bloquea scroll de fondo cuando está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Auto-play del carrusel
  useEffect(() => {
    if (!open || images.length <= 1 || hovering) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [open, images.length, intervalMs, hovering]);

  // Teclas de navegación / Escape
  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (images.length > 1 && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        setIndex((i) =>
          e.key === "ArrowLeft"
            ? (i - 1 + images.length) % images.length
            : (i + 1) % images.length
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length]);

  const go = (dir) => {
    if (images.length <= 1) return;
    setIndex((i) => (i + dir + images.length) % images.length);
  };

  // 🔹 base 20px + el “sube” configurado; sumamos safe area por si hay notch
  const bottomValue = `calc(${20 + (shiftUpPx ?? 0)}px + env(safe-area-inset-bottom, 0px))`;

  return (
    <>
      {/* Sticky Button */}
      <button
        onClick={() => setOpen(true)}
        style={{ ...stickyBtnStyle, bottom: bottomValue }}
      >
        <GrScheduleNew style={{ marginRight: 8 }} />
        {label}
      </button>

      {open && (
        <div
          style={backdropStyle}
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div
            style={{ ...cardStyle, maxWidth: maxCardWidth }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
          >
            <button onClick={() => setOpen(false)} style={closeBtnStyle} aria-label="Cerrar">
              ✕
            </button>

            {/* Visor centrado: margin auto + flex en slides */}
            <div
              style={{
                ...viewportStyle,
                width: `min(90vw, ${viewportMaxWidthPx}px)`,
                maxHeight: `${viewportMaxHeightVh}vh`,
                aspectRatio: ar,
                margin: "0 auto",             // 🔹 centra el visor dentro del card
              }}
            >
              {images.map((img, i) => (
                <div
                  key={i}
                  style={{
                    ...slideStyle,
                    transform: `translateX(${(i - index) * 100}%)`,
                    opacity: i === index ? 1 : 0,
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    style={imgStyle}
                    onLoad={(e) => {
                      const w = e.currentTarget.naturalWidth || 9;
                      const h = e.currentTarget.naturalHeight || 16;
                      setAr(`${w} / ${h}`); // preserva proporción real del flyer
                    }}
                  />
                </div>
              ))}

              {images.length > 1 && (
                <>
                  <button onClick={() => go(-1)} style={navLeftStyle} aria-label="Anterior">
                    ‹
                  </button>
                  <button onClick={() => go(1)} style={navRightStyle} aria-label="Siguiente">
                    ›
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div style={dotsBarStyle}>
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    style={{
                      ...dotStyle,
                      width: i === index ? 18 : 8,
                      background: i === index ? "#25D366" : "#c9c2d7",
                    }}
                    aria-label={`Ir al slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ===== Estilos ===== */
const stickyBtnStyle = {
  position: "fixed",
  right: 20,
  // bottom: dinámico desde el componente (bottomValue)
  zIndex: 9998,
  background: "#d9fdd3",
  color: "#075e54",
  border: "1px solid #b3e6ac",
  borderRadius: 12,
  padding: "10px 16px",
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
};

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "grid",
  placeItems: "center",
  zIndex: 9999,
  padding: 16,
};

const cardStyle = {
  position: "relative",
  width: "100%",
  borderRadius: 20,
  background: "#fff8ff",
  border: "1px solid #e6dfef",  // 🔧 corregido
  boxShadow: "0 14px 40px rgba(0,0,0,0.16)",
  overflow: "hidden",
};

const closeBtnStyle = {
  position: "absolute",
  right: 12,
  top: 12,
  width: 36,
  height: 36,
  borderRadius: 10,
  border: "1px solid #e6dfef",
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 16,
};

const viewportStyle = {
  position: "relative",
  background: "#0b0b0b",
  overflow: "hidden",
  userSelect: "none",
};

const slideStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",              // 🔹 centra el contenido del slide
  justifyContent: "center",     // 🔹
  alignItems: "center",         // 🔹
  transition: "transform 460ms ease, opacity 420ms ease",
};

const imgStyle = {
  width: "100%",
  height: "100%",
  objectFit: "contain", // preserva proporción del flyer
  objectPosition: "center",
  display: "block",
};

const navCommon = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 44,
  height: 44,
  borderRadius: "50%",
  border: "1px solid #e6dfef",
  background: "#ffffff",
  cursor: "pointer",
  fontSize: 28,
  boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
};
const navLeftStyle = { ...navCommon, left: 12 };
const navRightStyle = { ...navCommon, right: 12 };

const dotsBarStyle = {
  padding: "14px 0 8px",
  display: "flex",
  gap: 8,
  justifyContent: "center",
  alignItems: "center",
};

const dotStyle = {
  height: 8,
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
};
